import { Modal, PrimaryButton, ModalSizeEnum, FormInput, FormSelect, useTranslation as useUiTranslation } from '@systemeio/ui';
import { AddIcon } from '@systemeio/ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import FormUrlPathInput from 'shared/components/form-url-path-input';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { generatePathFromName } from 'shared/utils/path-utils';
import { getLocaleSelectOptions } from 'shared/utils/locale/get-locale-select-options';
import { WebsiteCreateInterface, WebsiteErrorsInterface } from 'modules/websites/types/website-interface';
import { FormSelectUserDomainWithWebsite } from './form-select-user-domain-with-website';
interface WebsiteCreateModalProps {
  disabled: boolean;
  defaultValues: WebsiteCreateInterface;
  errors: WebsiteErrorsInterface;
  userDomainsWithWebsiteSelect?: Array<{
    id: number;
    caption: string;
  }>;
  isDomainOccupied: (id: number) => boolean;
  onCreate: (data: WebsiteCreateInterface) => Promise<void>;
  afterLeave: () => void;
}
const WebsiteCreateModal = ({
  disabled,
  defaultValues,
  errors,
  userDomainsWithWebsiteSelect,
  isDomainOccupied,
  onCreate,
  afterLeave
}: WebsiteCreateModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    t: tUi
  } = useUiTranslation();
  const {
    user
  } = useUser();
  const [opened, setOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const getInitialFormValues = useCallback((): WebsiteCreateInterface => {
    const initialValues: WebsiteCreateInterface = {
      ...defaultValues
    };
    if (!initialValues.locale) {
      if (user?.dashboardLocale) {
        initialValues.locale = user.dashboardLocale;
      } else {
        const options = getLocaleSelectOptions(tUi);
        if (options?.length) {
          initialValues.locale = options[0]?.id || initialValues.locale;
        }
      }
    }
    return initialValues;
  }, [defaultValues, t, user?.dashboardLocale]);
  const [formValues, setFormValues] = useState<WebsiteCreateInterface>(getInitialFormValues);
  useEffect(() => {
    setFormValues(getInitialFormValues());
  }, [getInitialFormValues]);
  const handleClose = () => {
    setOpened(false);
  };
  const handleChange = <K extends keyof WebsiteCreateInterface,>(field: K, value: WebsiteCreateInterface[K]) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async () => {
    try {
      setIsFetching(true);
      await onCreate(formValues);
      setIsFetching(false);
      setOpened(false);
    } catch (e) {
      setIsFetching(false);
    }
  };
  return <>
      <PrimaryButton disabled={disabled} onClick={() => setOpened(true)} className="flex items-center gap-2">
        <AddIcon />
        <span className="hidden sm:flex"> {t('global.create')}</span>
      </PrimaryButton>
      <Modal opened={opened} onClose={handleClose} title={t('dashboard.website.title')} size={ModalSizeEnum.small} isFetching={isFetching} afterLeave={() => {
      setFormValues(getInitialFormValues());
      afterLeave();
    }}>
        <form className="flex flex-col gap-6 lg:gap-10">
          <div className="flex flex-col gap-5">
            <FormInput required key="website-name-create" className={'w-full'} label={t('global.name')} value={formValues.name} onChange={e_0 => handleChange('name', e_0.target.value)} error={errors.name} />
            <FormSelectUserDomainWithWebsite key="domainId-active-create" onChange={value_0 => handleChange('domainId', value_0)} value={formValues.domainId} data={userDomainsWithWebsiteSelect} errors={errors} isDomainOccupied={!!(formValues.domainId && isDomainOccupied(formValues.domainId))} />
            {!!formValues.domainId && <FormUrlPathInput key="home-page-path-create" error={errors.homePagePath} value={formValues.homePagePath} onChange={e_1 => handleChange('homePagePath', e_1.target.value)} onFocus={e_2 => {
            if (!e_2.target.value) {
              handleChange('homePagePath', generatePathFromName(formValues.name));
            }
          }} label={t('dashboard.website.home_page_title')} domainName={userDomainsWithWebsiteSelect?.find(el => el.id === formValues.domainId)?.caption} />}
            <FormSelect key="website-locale-create" withoutCloseIcon onChange={value_1 => handleChange('locale', value_1)} value={formValues.locale} label={t('global.language')} data={getLocaleSelectOptions(tUi)} placeholder={t('global.language')} error={errors.locale} />
          </div>
          <div className="flex justify-center">
            <PrimaryButton disabled={disabled} width="large" onClick={async (e_3: React.MouseEvent<HTMLButtonElement>) => {
            e_3.preventDefault();
            await handleSubmit();
          }} isFetching={isFetching} type="submit">
              {t('global.save')}
            </PrimaryButton>
          </div>
          <FieldErrorAndDescription error={errors.common} errorClassName={'text-center'} />
        </form>
      </Modal>
    </>;
};
export default WebsiteCreateModal;